import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Home, History, FileText, Ruler, Palette, MessageCircle, Gift, TrendingUp, Star, Package, ChevronDown, ChevronRight, ExternalLink, Plus, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

function LineChart({ data, valueKey }) {
  if (!data.length) return <p className="text-sm text-slate-400 text-center py-6">Aucune donnée</p>;
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  const min = Math.min(...data.map(d => d[valueKey]), 0);
  const range = max - min || 1;
  const h = 160, pad = 30, padLeft = 50, padRight = 20;
  const w = '100%';
  const points = data.map((d, i) => {
    const x = padLeft + (i / Math.max(data.length - 1, 1)) * (600 - padLeft - padRight);
    const y = pad + (1 - (d[valueKey] - min) / range) * (h - pad * 2);
    return { x, y, d };
  });
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${line} L${points[points.length - 1].x},${h - pad} L${points[0].x},${h - pad} Z`;

  return (
    <svg viewBox={`0 0 600 ${h}`} style={{ width: w, height: 'auto' }} className="overflow-visible">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Horizontal grid */}
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const y = pad + (1 - pct) * (h - pad * 2);
        const val = min + pct * range;
        return (
          <g key={pct}>
            <line x1={padLeft} y1={y} x2={600 - padRight} y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray={pct === 0 ? '0' : '4,4'} />
            <text x={padLeft - 8} y={y + 4} textAnchor="end" fill="#94a3b8" fontSize="10">{val >= 1000 ? `${(val / 1000).toFixed(0)}k` : Math.round(val)}€</text>
          </g>
        );
      })}
      {/* Area fill */}
      <path d={area} fill="url(#areaGrad)" />
      {/* Line */}
      <path d={line} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Points + labels */}
      {points.map((p, i) => {
        const [, mo] = p.d.month.split('-');
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke="#3b82f6" strokeWidth="2" />
            <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#1e40af" fontSize="10" fontWeight="600">
              {p.d[valueKey] >= 1000 ? `${(p.d[valueKey] / 1000).toFixed(1)}k` : p.d[valueKey]}
            </text>
            <text x={p.x} y={h - 8} textAnchor="middle" fill="#94a3b8" fontSize="10">{MONTHS_FR[parseInt(mo) - 1]}</text>
          </g>
        );
      })}
    </svg>
  );
}

const EMPTY_LINE = { nom: '', numero: '', taille: '' };

export default function RevendeurDashboard() {
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});
  const [claimDialog, setClaimDialog] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  // Quotes
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [pricings, setPricings] = useState({});
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteLines, setQuoteLines] = useState([{ product_id: '', product_name: '', quantity: 1 }]);
  const [creatingQuote, setCreatingQuote] = useState(false);

  // Size guide
  const [sizeGuideImages, setSizeGuideImages] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);

  // Personalization
  const [persoLines, setPersoLines] = useState([{ ...EMPTY_LINE }]);
  const [persoExporting, setPersoExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [meRes, dashRes, histRes, quotesRes, sizeRes, prodRes, pricingRes] = await Promise.all([
        axios.get(`${API}/api/client/me`, { withCredentials: true }),
        axios.get(`${API}/api/client/dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/client/orders/history`, { withCredentials: true }),
        axios.get(`${API}/api/client/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/client/size-guide`, { withCredentials: true }).catch(() => ({ data: { images: [] } })),
        axios.get(`${API}/api/client/products`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/client/product-pricing`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setClientInfo(meRes.data);
      setDashboard(dashRes.data);
      setHistory(histRes.data);
      setQuotes(quotesRes.data || []);
      setSizeGuideImages(sizeRes.data?.images || sizeRes.data || []);
      setProducts(prodRes.data || []);
      const pm = {};
      (pricingRes.data || []).forEach(p => { pm[p.product_id] = p.tiers || {}; });
      setPricings(pm);
    } catch (e) {
      if (e.response?.status === 401) navigate('/revendeur/login');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await axios.post(`${API}/api/client/logout`, {}, { withCredentials: true });
    navigate('/revendeur/login');
  };

  const handleClaim = async () => {
    setClaimLoading(true);
    try {
      const res = await axios.post(`${API}/api/client/loyalty/claim`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setClaimDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setClaimLoading(false); }
  };

  const getTierKey = (qty) => {
    if (qty >= 200) return "200+";
    if (qty >= 100) return "100-199";
    if (qty >= 40) return "40-99";
    if (qty >= 20) return "20-39";
    if (qty >= 10) return "10-19";
    if (qty >= 5) return "5-9";
    return String(qty);
  };

  const getUnitPrice = (productId, qty) => {
    const tiers = pricings[productId] || {};
    const key = getTierKey(qty);
    return parseFloat(tiers[key] || 0);
  };

  const handleCreateQuote = async () => {
    const validLines = quoteLines.filter(l => l.product_id && l.quantity > 0);
    if (!validLines.length) { toast.error('Ajoutez au moins un produit'); return; }
    setCreatingQuote(true);
    try {
      await axios.post(`${API}/api/client/quotes`, { lines: validLines }, { withCredentials: true });
      toast.success('Devis créé !');
      setShowQuoteForm(false);
      setQuoteLines([{ product_id: '', product_name: '', quantity: 1 }]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreatingQuote(false); }
  };

  const handleDownloadQuotePdf = async (quoteId, quoteNum) => {
    try {
      const res = await axios.get(`${API}/api/client/quotes/${quoteId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Devis_${quoteNum}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Erreur téléchargement'); }
  };

  // Personalization export
  const handlePersoExport = async () => {
    const validLines = persoLines.filter(l => l.nom.trim());
    if (!validLines.length) { toast.error('Ajoutez au moins un nom'); return; }
    setPersoExporting(true);
    try {
      const res = await axios.post(`${API}/api/personalization/export`, {
        client_name: `${clientInfo?.firstname || ''} ${clientInfo?.name || ''}`.trim(),
        lines: validLines,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'personnalisation.xlsx'; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error('Erreur export'); }
    finally { setPersoExporting(false); }
  };

  const historyByYear = {};
  history.forEach(o => { const yr = o.year || 'Inconnu'; if (!historyByYear[yr]) historyByYear[yr] = []; historyByYear[yr].push(o); });
  const sortedYears = Object.keys(historyByYear).sort((a, b) => b - a);

  const hasWhatsapp = clientInfo?.whatsapp_link;

  const sidebarItems = [
    { id: 'home', label: 'Accueil', icon: Home },
    { id: 'history', label: 'Historique', icon: History },
    { id: 'quotes', label: 'Devis', icon: FileText },
    { id: 'size-guide', label: 'Guide des tailles', icon: Ruler },
    { id: 'personalization', label: 'Personnalisation', icon: Palette },
    ...(hasWhatsapp ? [{ id: 'whatsapp', label: 'Rejoignez-nous sur WhatsApp', icon: MessageCircle }] : []),
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-9 object-contain" />
            <div>
              <p className="text-sm font-medium text-slate-900">{clientInfo?.firstname} {clientInfo?.name}</p>
              <p className="text-xs text-slate-500">{clientInfo?.company_name} — Revendeur</p>
            </div>
          </div>
          <Button data-testid="revendeur-logout-btn" onClick={handleLogout} variant="ghost" className="text-slate-600 text-sm">
            <LogOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0" data-testid="revendeur-sidebar">
          <nav className="p-3 space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              if (item.id === 'whatsapp') {
                return (
                  <a key={item.id} href={clientInfo?.whatsapp_link} target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-green-700 hover:bg-green-50">
                    <Icon size={18} /> <span>{item.label}</span> <ExternalLink size={12} className="ml-auto" />
                  </a>
                );
              }
              return (
                <button key={item.id} data-testid={`revendeur-tab-${item.id}`} onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <Icon size={18} /> <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 space-y-6 overflow-auto">

          {/* ===== ACCUEIL ===== */}
          {activeTab === 'home' && dashboard && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="pt-4 text-center">
                    <TrendingUp size={20} className="mx-auto text-blue-600 mb-1" />
                    <p className="text-2xl font-bold text-slate-900">{dashboard.total_revenue.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€</p>
                    <p className="text-xs text-slate-500">Chiffre d'affaires total HT</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-4 text-center">
                    <Star size={20} className="mx-auto text-amber-500 mb-1" />
                    <p className="text-2xl font-bold text-slate-900">{dashboard.available_points.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Points fidélité disponibles</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-4 text-center">
                    <Package size={20} className="mx-auto text-emerald-600 mb-1" />
                    <p className="text-2xl font-bold text-slate-900">{dashboard.total_orders}</p>
                    <p className="text-xs text-slate-500">Commandes totales</p>
                  </CardContent>
                </Card>
              </div>

              {/* CA Chart + Loyalty side by side */}
              <div className="grid grid-cols-5 gap-6">
                <Card className="border-slate-200 col-span-3">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">Chiffre d'affaires par mois (€ HT)</CardTitle>
                      <span className="text-lg font-bold text-slate-900">{dashboard.total_revenue.toLocaleString('fr-FR')} €</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <LineChart data={dashboard.revenue_chart} valueKey="revenue" />
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50/50 col-span-2">
                  <CardContent className="pt-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Gift size={22} className="text-amber-600" />
                        <h3 className="font-semibold text-slate-900">Programme de fidélité</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">À 15 000 points, recevez <strong>10 maillots ou t-shirts personnalisés offerts</strong> (hors frais de livraison).</p>
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Progression</span>
                          <span className="text-xs font-bold text-amber-700">{dashboard.available_points.toLocaleString()} / 15 000</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-3">
                          <div className="h-3 rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min((dashboard.available_points / 15000) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                    {dashboard.available_points >= 15000 && (
                      <Button data-testid="claim-reward-btn" className="bg-amber-600 hover:bg-amber-700 text-white gap-2 mt-4 w-full" onClick={() => setClaimDialog(true)}>
                        <Gift size={16} /> Réclamer mon lot
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Active Orders */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Commandes en cours ({dashboard.active_orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard.active_orders.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">Aucune commande en cours</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium">Commande</TableHead>
                          <TableHead className="font-medium">Date</TableHead>
                          <TableHead className="font-medium">Statut</TableHead>
                          <TableHead className="font-medium">Suivi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboard.active_orders.map(o => (
                          <TableRow key={o._id}>
                            <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                            <TableCell className="text-xs">{o.order_date?.slice(0, 10)}</TableCell>
                            <TableCell><Badge className="bg-blue-100 text-blue-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                            <TableCell>
                              {o.tracking_number ? (
                                <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                  <Package size={12} /> Suivi
                                </Button>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===== HISTORIQUE ===== */}
          {activeTab === 'history' && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Historique des commandes</CardTitle></CardHeader>
              <CardContent>
                {sortedYears.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune commande livrée</p>
                ) : (
                  <div className="space-y-3">
                    {sortedYears.map(year => (
                      <div key={year} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                          onClick={() => setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }))}>
                          <div className="flex items-center gap-2">
                            {expandedYears[year] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="font-semibold text-slate-900">{year}</span>
                          </div>
                          <Badge className="bg-slate-200 text-slate-700 border-0">{historyByYear[year].length}</Badge>
                        </button>
                        {expandedYears[year] && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-medium">Commande</TableHead>
                                <TableHead className="font-medium">Statut</TableHead>
                                <TableHead className="font-medium">Suivi</TableHead>
                                <TableHead className="font-medium">Date livraison</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {historyByYear[year].map(o => (
                                <TableRow key={o._id}>
                                  <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                                  <TableCell><Badge className="bg-emerald-100 text-emerald-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                                  <TableCell>
                                    {o.tracking_number ? (
                                      <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                        <Package size={12} /> Suivi
                                      </Button>
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                  </TableCell>
                                  <TableCell className="text-xs">{o.delivery_date ? new Date(o.delivery_date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===== DEVIS ===== */}
          {activeTab === 'quotes' && (
            <div className="space-y-6">
              {/* Create form */}
              {showQuoteForm ? (
                <Card className="border-blue-200 max-w-3xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Nouveau devis</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => setShowQuoteForm(false)}>Annuler</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700"><strong>Client :</strong> {clientInfo?.firstname} {clientInfo?.name} — {clientInfo?.company_name}</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-medium">Produit</TableHead>
                          <TableHead className="font-medium w-24">Quantité</TableHead>
                          <TableHead className="font-medium w-24">Prix unit.</TableHead>
                          <TableHead className="font-medium w-24">Total HT</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quoteLines.map((line, idx) => {
                          const up = getUnitPrice(line.product_id, line.quantity);
                          const lt = Math.round(up * line.quantity * 100) / 100;
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <select className="w-full h-8 text-sm border rounded px-2 bg-white"
                                  value={line.product_id}
                                  onChange={e => {
                                    const prod = products.find(p => p.id === e.target.value);
                                    setQuoteLines(prev => prev.map((l, i) => i === idx ? { ...l, product_id: e.target.value, product_name: prod?.name || '' } : l));
                                  }}>
                                  <option value="">Sélectionner...</option>
                                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                              </TableCell>
                              <TableCell>
                                <Input type="number" min="1" className="h-8 text-sm" value={line.quantity}
                                  onChange={e => setQuoteLines(prev => prev.map((l, i) => i === idx ? { ...l, quantity: parseInt(e.target.value) || 0 } : l))} />
                              </TableCell>
                              <TableCell className="text-sm font-medium">{up > 0 ? `${up.toFixed(2)}€` : '—'}</TableCell>
                              <TableCell className="text-sm font-semibold">{lt > 0 ? `${lt.toFixed(2)}€` : '—'}</TableCell>
                              <TableCell>
                                {quoteLines.length > 1 && (
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setQuoteLines(prev => prev.filter((_, i) => i !== idx))}>
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <div className="flex items-center justify-between mt-3">
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => setQuoteLines(prev => [...prev, { product_id: '', product_name: '', quantity: 1 }])}>
                        <Plus size={14} /> Ajouter un produit
                      </Button>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Total HT : <strong>{quoteLines.reduce((sum, l) => sum + getUnitPrice(l.product_id, l.quantity) * l.quantity, 0).toFixed(2)}€</strong></p>
                        <Button className="mt-2 bg-blue-600 hover:bg-blue-700 gap-1" onClick={handleCreateQuote} disabled={creatingQuote}>
                          <FileText size={14} /> {creatingQuote ? 'Création...' : 'Créer le devis'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-slate-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Mes devis ({quotes.length})</CardTitle>
                      <Button data-testid="create-quote-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={() => setShowQuoteForm(true)}>
                        <Plus size={14} /> Nouveau devis
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {quotes.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Aucun devis</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-medium">N° Devis</TableHead>
                            <TableHead className="font-medium">Date</TableHead>
                            <TableHead className="font-medium">Statut</TableHead>
                            <TableHead className="font-medium">Total HT</TableHead>
                            <TableHead className="font-medium">Total TTC</TableHead>
                            <TableHead className="font-medium">PDF</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {quotes.map(q => (
                            <TableRow key={q._id}>
                              <TableCell className="font-medium text-sm">{q.quote_number}</TableCell>
                              <TableCell className="text-xs">{(q.created_at || q.date || '').slice(0, 10)}</TableCell>
                              <TableCell><Badge className={`text-xs ${q.status === 'accepté' ? 'bg-emerald-100 text-emerald-800' : q.status === 'envoyé' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>{(q.status || 'en attente').toUpperCase()}</Badge></TableCell>
                              <TableCell className="text-xs font-semibold">{(q.total_ht || 0).toLocaleString('fr-FR')}€</TableCell>
                              <TableCell className="text-xs font-semibold">{(q.total_ttc || 0).toLocaleString('fr-FR')}€</TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={() => handleDownloadQuotePdf(q._id, q.quote_number)}>
                                  <Download size={12} /> PDF
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ===== GUIDE DES TAILLES ===== */}
          {activeTab === 'size-guide' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="size-guide-section">
              <h2 style={{ color: '#c3263b', borderLeft: '5px solid #c3263b' }} className="pl-4 mb-5 uppercase text-sm font-bold">Guide des tailles</h2>
              <div className="flex gap-5 flex-wrap">
                {/* Adultes */}
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ADULTES</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['XS','45-50 kg','160-166 cm'],['S','50-55 kg','166-170 cm'],['M','55-65 kg','170-175 cm'],['L','65-75 kg','175-180 cm'],['XL','75-85 kg','180-185 cm'],['2XL','85-95 kg','185-190 cm'],['3XL','95-110 kg','190-195 cm'],['4XL','110-115 kg','195-197 cm'],['5XL','115-130 kg','197-199 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Enfants */}
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ENFANTS (KIDS)</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['2 ans','12-17 kg','105-122 cm'],['4 ans','17-22 kg','122-135 cm'],['6 ans','22-27 kg','135-145 cm'],['8 ans','27-35 kg','145-155 cm'],['10 ans','35-45 kg','155-160 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Size guide zoom dialog */}
          <Dialog open={!!zoomedImage} onOpenChange={() => setZoomedImage(null)}>
            <DialogContent className="max-w-4xl">
              <DialogHeader><DialogTitle>Guide des tailles</DialogTitle></DialogHeader>
              {zoomedImage && <img src={zoomedImage} alt="Guide taille" className="w-full object-contain rounded" />}
            </DialogContent>
          </Dialog>

          {/* ===== PERSONNALISATION ===== */}
          {activeTab === 'personalization' && (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Personnalisation</CardTitle>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handlePersoExport} disabled={persoExporting}>
                    <Download size={14} /> {persoExporting ? 'Export...' : 'Exporter Excel'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700"><strong>Client :</strong> {clientInfo?.firstname} {clientInfo?.name} — {clientInfo?.company_name}</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-medium w-10">#</TableHead>
                      <TableHead className="font-medium">Nom</TableHead>
                      <TableHead className="font-medium">Numéro</TableHead>
                      <TableHead className="font-medium">Taille</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {persoLines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs text-slate-500">{idx + 1}</TableCell>
                        <TableCell><Input value={line.nom} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, nom: e.target.value } : l))} placeholder="Nom" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.numero} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, numero: e.target.value } : l))} placeholder="N°" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.taille} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, taille: e.target.value } : l))} placeholder="Taille" className="h-8 text-sm" /></TableCell>
                        <TableCell>
                          {persoLines.length > 1 && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setPersoLines(prev => prev.filter((_, i) => i !== idx))}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => setPersoLines(prev => [...prev, { ...EMPTY_LINE }])}>
                  <Plus size={14} /> Ajouter une ligne
                </Button>
              </CardContent>
            </Card>
          )}
        </main>
      </div>

      {/* Claim Dialog */}
      <Dialog open={claimDialog} onOpenChange={setClaimDialog}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift size={20} className="text-amber-600" /> Réclamer votre lot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Vous êtes sur le point d'utiliser <strong>15 000 points</strong> pour recevoir <strong>10 maillots ou t-shirts personnalisés offerts</strong> (hors frais de livraison).</p>
            <p className="text-sm text-slate-600">L'équipe Panther vous contactera pour finaliser votre commande.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setClaimDialog(false)}>Annuler</Button>
              <Button data-testid="confirm-claim-btn" className="bg-amber-600 hover:bg-amber-700" onClick={handleClaim} disabled={claimLoading}>
                {claimLoading ? '...' : 'Confirmer la réclamation'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
